import { useEffect } from 'react'

import { type Capacidad, type Preset, useCatalogoDeCapacidades } from '../api/capacidades'
import { Aviso, Esqueleto, Tarjeta } from '../componentes/ui'
import { useValor } from '../motor/Formulario'
import { CasillasDeCapacidades, capacidadesDelPreset } from './CasillasDeCapacidades'

/**
 * Lo que va a poder hacer la persona invitada, dentro del formulario de alta (el editor
 * genérico de `usuarios`). Escribe en el propio formulario —`preset`, `capacidades` y
 * `rol`, que es el preset con el que nace su cuenta—, así que viaja en el cuerpo de
 * `POST /api/panel/usuarios/invitar` como un campo más.
 *
 * Hasta que el catálogo llega, `capacidades` queda sin poner: el núcleo entiende «las del
 * preset». En cuanto llega, se escriben las del preset, para que lo que se marque después
 * parta de ahí. `presetsDisponibles` acota lo que se ofrece: nombrar propietario es cosa
 * del dueño (el núcleo lo rechaza igual; aquí no se ofrece la puerta).
 */
export const PermisosDeInvitacion = ({ presetsDisponibles }: { presetsDisponibles?: Preset[] }) => {
  const { data: catalogo, isPending, error } = useCatalogoDeCapacidades()
  const preset = useValor<Preset>('preset')
  const rol = useValor<string>('rol')
  const capacidades = useValor<Capacidad[]>('capacidades')
  const presetActual: Preset = preset.valor ?? 'editor'

  useEffect(() => {
    if (!catalogo || capacidades.valor !== undefined) return
    capacidades.cambiar(capacidadesDelPreset(catalogo, presetActual))
    // Solo cuando llega el catálogo: `capacidades` cambia con cada casilla y aquí no interesa.
  }, [catalogo]) // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Tarjeta>
      <p className="mb-1 text-xs font-medium tracking-wide text-tenue uppercase">Qué va a poder hacer</p>
      <p className="mb-4 text-sm text-tenue">Un punto de partida y, si hace falta, casilla a casilla. Se puede cambiar después desde su ficha.</p>
      {isPending && <Esqueleto filas={4} />}
      {error && <Aviso tono="error">No se ha podido leer la lista de permisos. Se invitará con los del punto de partida; podrás afinarlos desde su ficha.</Aviso>}
      {catalogo && (
        <CasillasDeCapacidades
          cambiar={(e) => {
            preset.cambiar(e.preset)
            rol.cambiar(e.preset)
            capacidades.cambiar(e.capacidades)
          }}
          catalogo={catalogo}
          eleccion={{ preset: presetActual, capacidades: capacidades.valor ?? capacidadesDelPreset(catalogo, presetActual) }}
          presetsDisponibles={presetsDisponibles}
        />
      )}
    </Tarjeta>
  )
}
